import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'

type SqliteDB = Awaited<
  ReturnType<typeof import('@tauri-apps/plugin-sql').default.load>
>

let dbPromise: Promise<SqliteDB> | null = null

async function getDb(): Promise<SqliteDB> {
  if (!dbPromise) {
    dbPromise = (async () => {
      const mod = await import('@tauri-apps/plugin-sql')
      const db = await mod.default.load('sqlite:cache.db')
      await db.execute(`CREATE TABLE IF NOT EXISTS query_cache (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at INTEGER NOT NULL
      )`)
      return db
    })().catch(error => {
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export async function removeSQLiteCaches(prefix: string): Promise<void> {
  const db = await getDb()
  await db.execute(
    'DELETE FROM query_cache WHERE substr(key, 1, length($1)) = $1',
    [prefix],
  )
}

export async function createSQLitePersister(
  cacheKey: string,
  scopePrefix = cacheKey,
): Promise<Persister> {
  const db = await getDb()
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const value = JSON.stringify(client)
        await db.execute(
          `INSERT OR REPLACE INTO query_cache (key, value, updated_at) VALUES ($1, $2, $3)`,
          [cacheKey, value, Date.now()],
        )
      } catch {
        console.warn('[QueryCache:SQLite] Failed to persist client')
      }
    },
    restoreClient: async () => {
      try {
        const rows = await db.select<{ value: string }[]>(
          `SELECT value FROM query_cache WHERE key = $1 OR (substr(key, 1, length($2)) = $2 AND substr(key, length($2) + 1, 7) != 'schema-') ORDER BY CASE WHEN key = $1 THEN 0 ELSE 1 END, updated_at DESC LIMIT 1`,
          [cacheKey, scopePrefix],
        )
        if (rows.length === 0) return undefined
        return JSON.parse(rows[0].value) as PersistedClient
      } catch {
        console.warn('[QueryCache:SQLite] Failed to restore client')
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeSQLiteCaches(scopePrefix)
      } catch {
        console.warn('[QueryCache:SQLite] Failed to remove client')
      }
    },
  }
}
